import { TableCell, TableRow, Typography } from '@mui/material';
import { GridLayout } from 'shared/ui/grid-layout';
import { useSnack } from 'shared/ui/snack';
import { useCashBalanceData } from './cash-balance.hook';
import { CashBalanceDto } from '@sr/dto';
import { useSaveFinalBalance } from './save-final-balance.hook';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useState } from 'react';

const columns = ['Расчетный счет', 'Остаток на 1-ое число месяца', 'Поступления за месяц', 'Списания за месяц', 'Остаток на текущее число (на последнее число месяца)', ''];

const CashBalanceRow = ({
  row,
  year,
  month,
  onSaveSuccess,
  onSaveError,
}: {
  row: CashBalanceDto;
  year: number;
  month: number;
  onSaveSuccess: () => void;
  onSaveError: (error: unknown) => void;
}) => {
  const saveBalance = useSaveFinalBalance();
  const [newBalanceValue, setNewBalanceValue] = useState<number>(0);

  const handleSaveBalance = async () => {
    try {
      await saveBalance({
        month,
        year,
        expenses: row.expenses,
        income: row.income,
        startDateBalanceValue: row.isNewBankAccount ? newBalanceValue : row.startDateBalanceValue,
        endDateBalanceValue: row.endDateBalanceValue,
        accountId: row.bankAccountId,
        isNewBankAccount: row.isNewBankAccount,
      });
      onSaveSuccess();
    } catch (error) {
      onSaveError(error);
    }
  };

  return (
    <TableRow sx={{ backgroundColor: '#e6f4ea', '&:hover': { backgroundColor: '#d7ecdd' } }}>
      <TableCell align="center">{row.bankAccountTitle}</TableCell>
      {row.isAvailableForSave || row.isNewBankAccount ? (
        <>
          <TableCell align="center">
            {row.isNewBankAccount ? (
              <TextField
                fullWidth
                required
                label="Сумма внесения"
                type="number"
                value={newBalanceValue}
                onChange={(e) => setNewBalanceValue(Number(e.target.value))}
                size="small"
              />
            ) : (
              row.startDateBalanceValue.toFixed(2)
            )}
          </TableCell>
          <TableCell align="center">{row.income.toFixed(2)}</TableCell>
          <TableCell align="center">{row.expenses.toFixed(2)}</TableCell>
          <TableCell align="center">{row.endDateBalanceValue.toFixed(2)}</TableCell>
        </>
      ) : (
        <TableCell align="center" colSpan={4}>
          <Typography color="error" variant="subtitle2">
            Чтобы увидеть остатки, закройте предыдущий месяц
          </Typography>
        </TableCell>
      )}
      <TableCell align="center">
        {((!row.isSavedBalance && (month < new Date().getMonth() || year < new Date().getFullYear())) || row.isNewBankAccount) && (
          <Button variant="contained" disabled={!row.isAvailableForSave && !row.isNewBankAccount} type="submit" autoFocus onClick={handleSaveBalance}>
            Сохранить
          </Button>
        )}
        {row?.user && (
          <span>
            {row.user.secondName} {row.user.firstName} {row.user.middleName} подтвердил
          </span>
        )}
      </TableCell>
    </TableRow>
  );
};

const totalCellSx = {
  fontWeight: 700,
  fontSize: '0.95rem',
};

const CashBalanceTotal = (rows: CashBalanceDto[]) => {
  const sumBy = (selector: (row: CashBalanceDto) => number) => rows.reduce((acc, curr) => (curr.isAvailableForSave ? selector(curr) : 0) + acc, 0);

  const TotalCell = ({ value }: { value: number }) => {
    const isNegative = value < 0;
    return (
      <TableCell
        align="center"
        sx={{
          ...totalCellSx,
          color: isNegative ? 'error.main' : 'inherit',
          backgroundColor: isNegative ? '#f8d7da' : '#d6e4f0',
        }}
      >
        {value.toFixed(2)}
      </TableCell>
    );
  };

  return (
    <TableRow>
      <TableCell align="center" sx={{ ...totalCellSx, backgroundColor: '#d6e4f0' }}>
        ИТОГО:
      </TableCell>
      <TotalCell value={sumBy((row) => row.startDateBalanceValue)} />
      <TotalCell value={sumBy((row) => row.income)} />
      <TotalCell value={sumBy((row) => row.expenses)} />
      <TotalCell value={sumBy((row) => row.endDateBalanceValue)} />
      <TableCell align="center" sx={{ backgroundColor: '#d6e4f0' }}></TableCell>
    </TableRow>
  );
};

export const CashBalanceGrid = ({ year, month }: { year: number; month: number }) => {
  const { rows, isLoading, error } = useCashBalanceData(year, month);
  const { showSuccess, showError } = useSnack();

  const handleSaveSuccess = () => {
    showSuccess('Данные успешно сохранены');
  };

  const handleSaveError = (error: unknown) => {
    showError('Ошибка при сохранении данных');
  };

  return (
    <GridLayout
      columns={columns}
      items={rows}
      isLoading={isLoading}
      error={error}
      footer={CashBalanceTotal}
      itemRender={(row) => <CashBalanceRow year={year} month={month} key={row.bankAccountId} row={row} onSaveSuccess={handleSaveSuccess} onSaveError={handleSaveError} />}
      noItemsText="Нет оплаченных счетов за выбранный период"
    />
  );
};
